/**
 * <s-discount-tier> — live quantity-discount nudge (see snippets/discount-tier-hint.liquid).
 *
 * Product mode (data-quantity-input): count = data-in-cart + the selected quantity. Re-renders
 * the reached tier ("10% off liberado!") and the next tier ("+2 peças → 15% off") whenever the
 * quantity input changes, and when a cart-mode element reports a fresh count for its entry.
 *
 * Broadcast mode (data-broadcast, cart drawer / cart page): on connect (i.e. after every cart
 * partial refresh) dispatches `discount-tier:cart-count` { group, inCart, codeApplied } on document.
 * codeApplied = a discount code is on the cart; product-mode nudges hide their [data-tier-wrap]
 * while it is true and reappear when it turns false.
 *
 * Templates come from Liquid translations: __P__ = percent; the plural "next" strings were
 * rendered with count 1 (data-next-one) and 9999 (data-next-other, 9999 replaced here).
 */
class SDiscountTier extends HTMLElement {
  #ac;
  #inCart = 0;
  #tiers = [];

  static observedAttributes = ['data-in-cart', 'data-code-applied'];

  // Partial refreshes may morph a broadcaster in place instead of replacing it; re-announce then too.
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected || !this.hasAttribute('data-broadcast')) return;
    this.#broadcast();
  }

  #broadcast() {
    document.dispatchEvent(new CustomEvent('discount-tier:cart-count', {
      detail: {
        group: this.dataset.group,
        inCart: Number(this.dataset.inCart) || 0,
        codeApplied: this.dataset.codeApplied === 'true',
      },
    }));
  }

  connectedCallback() {
    this.#inCart = Number(this.dataset.inCart) || 0;

    if (this.hasAttribute('data-broadcast')) {
      this.#broadcast();
      return;
    }
    if (!this.dataset.quantityInput) return;

    const quantities = (this.dataset.quantities || '').split(',').map(Number);
    const percents = (this.dataset.percents || '').split(',').map(Number);
    this.#tiers = quantities
      .map((quantity, index) => ({ quantity, percent: percents[index] }))
      .filter((tier) => tier.quantity > 0 && tier.percent > 0)
      .sort((a, b) => a.quantity - b.quantity);

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const rerender = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.id === this.dataset.quantityInput || target.matches('s-quantity-selector')) this.#render();
    };
    document.addEventListener('s-quantity:change', rerender, opts);
    document.addEventListener('input', rerender, opts);
    document.addEventListener('change', rerender, opts);
    document.addEventListener('discount-tier:cart-count', (event) => {
      if (event.detail?.group !== this.dataset.group) return;
      this.#inCart = Number(event.detail.inCart) || 0;
      // A discount code on the cart hides the nudge; removing it brings the nudge back.
      const wrap = this.closest('[data-tier-wrap]');
      if (wrap) wrap.hidden = Boolean(event.detail.codeApplied);
      this.#render();
    }, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #selected() {
    const input = document.getElementById(this.dataset.quantityInput);
    const value = parseInt(input?.value, 10);
    if (Number.isFinite(value) && value > 0) return value;
    return Number(this.dataset.selected) || 1;
  }

  #render() {
    if (!this.#tiers.length) return;
    const count = this.#inCart + this.#selected();
    let reached = null;
    let next = null;
    for (const tier of this.#tiers) {
      if (tier.quantity <= count) reached = tier;
      else if (!next) next = tier;
    }

    const applied = this.querySelector('[data-tier-applied]');
    if (applied) {
      applied.hidden = !reached;
      if (reached) applied.textContent = (this.dataset.appliedTemplate || '').replace('__P__', reached.percent);
    }

    const nextWrap = this.querySelector('[data-tier-next]');
    if (nextWrap) {
      nextWrap.hidden = !next;
      if (next) {
        const missing = next.quantity - count;
        const piecesTemplate = missing === 1 ? this.dataset.nextOne : (this.dataset.nextOther || '').replace('9999', missing);
        const pieces = this.querySelector('[data-tier-pieces]');
        const percent = this.querySelector('[data-tier-percent]');
        if (pieces) pieces.textContent = piecesTemplate || '';
        if (percent) percent.textContent = (this.dataset.percentTemplate || '').replace('__P__', next.percent);
      }
    }
  }
}

if (!customElements.get('s-discount-tier')) {
  customElements.define('s-discount-tier', SDiscountTier);
}
